import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Empty, Popconfirm, Spin } from 'antd';
import dayjs from 'dayjs';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import EventDetailsV2Context from '@totem/components/services/threats/eventsDashboard/details/EventDetailsV2Context';
import RawEventModal from '@totem/components/services/threats/eventsDashboard/details/RawEventModal';
import { OccurrenceDay } from '@totem/components/services/threats/eventsDashboard/details/types';
import { SeverityPill } from '@totem/components/services/threats/eventsDashboard/report/pills';
import {
  EMPTY_ID,
  formatDate,
  formatDateTime,
  getSeverity,
  ticketTypeFromNumber,
} from '@totem/components/services/threats/eventsDashboard/report/severity';
import TicketAssociateModal from '@totem/components/services/threats/ticketLinkModal/TicketAssociateModal';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Event } from '@totem/types/events';

import './eventDetailsV2.css';

const REPORT_PATH = '/dashboard/events2';

const OccurrenceChart = ({ days }: { days: OccurrenceDay[] }) => {
  if (days.length === 0) {
    return <div styleName="empty">No per-day occurrence history.</div>;
  }

  const width = 620;
  const height = 70;
  const max = Math.max(1, ...days.map((day) => day.count));
  const points = days.map((day, idx) => [
    days.length === 1 ? width / 2 : (idx / (days.length - 1)) * width,
    height - 4 - (day.count / max) * (height - 8),
  ]);
  const line = points.map((point) => point.join(',')).join(' ');

  return (
    <>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        styleName="chart"
        role="img"
        aria-label="Occurrences per day"
      >
        <polygon
          styleName="chart-area"
          points={`${points[0][0]},${height} ${line} ${points[points.length - 1][0]},${height}`}
        />
        <polyline styleName="chart-line" points={line} />
      </svg>
      <div styleName="chart-foot">
        <span>{dayjs(days[0].day).format('MMM D')}</span>
        <span>peak {max}/day</span>
        <span>{dayjs(days[days.length - 1].day).format('MMM D')}</span>
      </div>
    </>
  );
};

const EventDetailsV2Layout = () => {
  const navigate = useNavigate();
  const { userProfile } = useContext(UserProfileContext);
  const {
    event,
    loading,
    notFound,
    sending,
    setStatus,
    createTicket,
    associateTicket,
  } = useContext(EventDetailsV2Context);
  const [showAssociate, setShowAssociate] = useState<boolean>(false);
  const [showRaw, setShowRaw] = useState<boolean>(false);

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(REPORT_PATH);
    }
  };

  if (!event) {
    return (
      <div styleName="detail">
        <a styleName="back" onClick={goBack}>
          ← Back to Events
        </a>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <Empty
            description={
              notFound
                ? 'That issue is no longer available. It may have been removed, or it is outside the buildings you can see.'
                : 'The event could not be loaded.'
            }
          />
        )}
      </div>
    );
  }

  const severity = getSeverity(event.severity);
  const ticket = event.tickets[0] ?? null;
  const days = Math.max(
    0,
    dayjs(event.clearedAt ?? undefined).diff(
      dayjs(event.eventTime),
      'day',
      true,
    ),
  );
  const spanDays = Math.max(
    1,
    Math.floor(
      dayjs(event.lastOccurrence).diff(dayjs(event.eventTime), 'day', true),
    ),
  );
  const hasBuilding = event.building?.id && event.building.id !== EMPTY_ID;
  const constraints = {
    regionId: event.region.id,
    buildingId: event.building?.id ?? EMPTY_ID,
    controlSystemId: event.controlSystem.id,
  };
  const canManage =
    !event.resolved &&
    permissions.eventManager.hasPermissions(userProfile, constraints);
  const canTicket =
    !event.resolved &&
    ticket === null &&
    permissions.ticketingCreator.hasPermissions(userProfile, constraints);

  const canViewRaw =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);

  const statusFlag = event.resolved ? (
    <span styleName="flag flag-green">Resolved</span>
  ) : ticket ? (
    <span styleName="flag flag-amber">Ticket opened</span>
  ) : event.status === 'ACKNOWLEDGED' ? (
    <span styleName="flag flag-amber">Acknowledged</span>
  ) : (
    <span styleName="flag flag-red">Open</span>
  );

  const responseStatus = event.resolved
    ? 'Resolved'
    : ticket
      ? 'Ticket opened'
      : event.status === 'ACKNOWLEDGED'
        ? 'Acknowledged'
        : 'Being reviewed';

  return (
    <Spin spinning={sending || loading}>
      <div styleName="detail">
        <a styleName="back" onClick={goBack}>
          ← Back to Events
        </a>

        <div
          styleName="hero"
          style={{
            borderLeftColor: event.resolved ? '#22c55e' : severity.color,
          }}
        >
          <div
            styleName="badge"
            style={
              event.resolved
                ? { background: 'rgba(34, 197, 94, 0.14)', color: '#15803d' }
                : { background: `${severity.color}22`, color: severity.color }
            }
          >
            {event.resolved ? 'Resolved' : severity.label}
          </div>
          <div>
            <h1 styleName="title">{event.eventType}</h1>
            <div styleName="location">
              {[
                hasBuilding ? event.building.name : 'Unassigned building',
                event.device.name,
                event.organization.name,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <div styleName="description">{event.description}</div>
            <div styleName="flags">
              {statusFlag}
              <span
                styleName={
                  days >= 7 && !event.resolved ? 'flag flag-amber' : 'flag'
                }
              >
                {event.resolved
                  ? `resolved after ${Math.round(days)}d`
                  : `${Math.round(days)}d open`}
              </span>
              <span styleName="flag">
                {event.occurrenceCount} occurrence
                {event.occurrenceCount === 1 ? '' : 's'}
              </span>
              <span styleName="flag">{event.category.name}</span>
            </div>
            <div styleName="actions">
              {ticket && (
                <Link to={`/dashboard/ticket/${ticket.id}`}>
                  <Button type="primary">
                    {ticketTypeFromNumber(ticket.number)} · {ticket.number} ›
                  </Button>
                </Link>
              )}
              {canManage && event.status !== 'ACKNOWLEDGED' && (
                <Button onClick={() => setStatus('ACKNOWLEDGED')}>
                  Acknowledge
                </Button>
              )}
              {canManage && (
                <Popconfirm
                  title="Clear this event?"
                  description="It moves to the resolved events."
                  okText="Clear"
                  onConfirm={() => setStatus('CLEARED')}
                >
                  <Button>Clear</Button>
                </Popconfirm>
              )}
              {canTicket && (
                <>
                  <Popconfirm
                    title="Create a ticket for this event?"
                    okText="Create ticket"
                    onConfirm={createTicket}
                  >
                    <Button>Create ticket</Button>
                  </Popconfirm>
                  <Button onClick={() => setShowAssociate(true)}>
                    Link existing ticket
                  </Button>
                </>
              )}
              {canViewRaw && (
                <IBTooltip
                  content={permissions.internalSystemAdministrator.tooltip}
                >
                  <Button type="dashed" onClick={() => setShowRaw(true)}>
                    View raw event
                  </Button>
                </IBTooltip>
              )}
            </div>
          </div>
        </div>

        <div styleName="grid">
          <div styleName="column">
            <div styleName="card">
              <h2 styleName="card-title">Occurrence summary</h2>
              <div styleName="stats">
                <div>
                  <div styleName="stat-label">Occurrences</div>
                  <div styleName="stat-value" style={{ color: severity.color }}>
                    {event.occurrenceCount}
                  </div>
                  <div styleName="stat-sub">grouped into 1 event</div>
                </div>
                <div>
                  <div styleName="stat-label">Rate</div>
                  <div styleName="stat-value">
                    {(event.occurrenceCount / spanDays).toFixed(1)}/day
                  </div>
                  <div styleName="stat-sub">
                    over {spanDays} day{spanDays === 1 ? '' : 's'}
                  </div>
                </div>
                <div>
                  <div styleName="stat-label">First seen</div>
                  <div styleName="stat-value">
                    {formatDate(event.eventTime)}
                  </div>
                  <div styleName="stat-sub">opened</div>
                </div>
                <div>
                  <div styleName="stat-label">
                    {event.resolved ? 'Resolved' : 'Last seen'}
                  </div>
                  <div styleName="stat-value">
                    {formatDate(
                      event.resolved ? event.clearedAt : event.lastOccurrence,
                    )}
                  </div>
                  <div styleName="stat-sub">
                    {event.resolved ? 'cleared' : 'most recent'}
                  </div>
                </div>
              </div>
              <OccurrenceChart days={event.occurrencesByDay} />
            </div>

            <div styleName="card">
              <h2 styleName="card-title">What this means</h2>
              {event.customerInfo.whatItMeans || (
                <span styleName="empty">{event.category.description}.</span>
              )}
            </div>
            <div styleName="card">
              <h2 styleName="card-title">Why it matters</h2>
              {event.customerInfo.whyTicketRaised || (
                <span styleName="empty">
                  IB reviews every Minor or higher event and opens a ticket when
                  action is needed.
                </span>
              )}
            </div>
            {event.customerInfo.impact && (
              <div styleName="card card-doing">
                <h2 styleName="card-title">Impact</h2>
                {event.customerInfo.impact}
              </div>
            )}
            {event.details && event.details !== event.description && (
              <div styleName="card">
                <h2 styleName="card-title">Details</h2>
                <div style={{ whiteSpace: 'pre-wrap' }}>{event.details}</div>
              </div>
            )}
            <div styleName="card">
              <h2 styleName="card-title">Activity</h2>
              {event.activity.length === 0 ? (
                <span styleName="empty">No activity recorded yet.</span>
              ) : (
                event.activity
                  .slice()
                  .reverse()
                  .map((entry) => (
                    <div
                      key={`${entry.createdAt}-${entry.description}`}
                      styleName="activity"
                    >
                      <span styleName="sub">
                        {formatDateTime(entry.createdAt)}
                      </span>
                      <span>
                        {entry.description}
                        <span styleName="sub">
                          {' '}
                          ·{' '}
                          {entry.createdBy.startsWith('RULE_')
                            ? 'Automatic'
                            : entry.createdBy}
                        </span>
                      </span>
                    </div>
                  ))
              )}
            </div>
          </div>

          <div styleName="column">
            <div styleName="card">
              <h2 styleName="card-title">Asset</h2>
              <div styleName="facts">
                <span styleName="fact-label">Device</span>
                <span>
                  {event.device.id !== EMPTY_ID && event.device.name ? (
                    <Link to={`/dashboard/devices/${event.device.id}`}>
                      {event.device.name}
                    </Link>
                  ) : (
                    '—'
                  )}
                </span>
                <span styleName="fact-label">Building system</span>
                <span>{event.controlSystem.name || '—'}</span>
                <span styleName="fact-label">Service area</span>
                <span>{event.category.name}</span>
                <span styleName="fact-label">Source</span>
                <span>{event.sourceType || '—'}</span>
              </div>
            </div>
            <div styleName="card">
              <h2 styleName="card-title">Location</h2>
              <div styleName="facts">
                <span styleName="fact-label">Building</span>
                <span>
                  {hasBuilding ? (
                    <Link to={`/dashboard/buildings/${event.building.id}`}>
                      {event.building.name}
                    </Link>
                  ) : (
                    'Unassigned'
                  )}
                </span>
                <span styleName="fact-label">Address</span>
                <span>
                  {[
                    event.building?.address,
                    [event.building?.city, event.building?.state]
                      .filter(Boolean)
                      .join(', '),
                    event.building?.zipCode,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
                <span styleName="fact-label">Region</span>
                <span>{event.region.name || '—'}</span>
                <span styleName="fact-label">Customer</span>
                <span>{event.organization.name}</span>
              </div>
            </div>
            <div styleName="card">
              <h2 styleName="card-title">IB response</h2>
              <div styleName="facts">
                <span styleName="fact-label">Status</span>
                <span>{responseStatus}</span>
                <span styleName="fact-label">Linked ticket</span>
                <span>
                  {event.tickets.length === 0
                    ? 'None yet'
                    : event.tickets.map((linked) => (
                        <div key={linked.id}>
                          <Link to={`/dashboard/ticket/${linked.id}`}>
                            {linked.number} ›
                          </Link>
                        </div>
                      ))}
                </span>
              </div>
            </div>
            <div styleName="card">
              <h2 styleName="card-title">Other open issues here</h2>
              {event.siblings.length === 0 ? (
                <span styleName="empty">
                  No other open issues at this building.
                </span>
              ) : (
                event.siblings.map((sibling) => (
                  <Link
                    key={sibling.id}
                    to={`${REPORT_PATH}/${sibling.id}`}
                    styleName="sibling"
                  >
                    <SeverityPill severity={sibling.severity} />
                    <div styleName="sibling-title">{sibling.eventType}</div>
                    <div styleName="sub">{sibling.description}</div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        {canViewRaw && (
          <RawEventModal
            eventId={event.id}
            open={showRaw}
            onClose={() => setShowRaw(false)}
          />
        )}
        {showAssociate && (
          <TicketAssociateModal
            event={{ id: event.id, description: event.description } as Event}
            visible={showAssociate}
            onClose={() => setShowAssociate(false)}
            onAssociateTicket={(eventId, selected) => {
              setShowAssociate(false);

              associateTicket(selected.ticket.id, selected.ticket.number);
            }}
          />
        )}
      </div>
    </Spin>
  );
};

export default EventDetailsV2Layout;
